"use client";

import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { Image } from "@heroui/image";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useEffect, useState } from "react";
import { Button } from "@heroui/button";

type Props = {
  img?: File[];
};

type TraitData = {
  name: string;
  label: string;
  description: string;
};

const traits: TraitData[] = [
  {
    name: "mw_me",
    label: "MW - ME",
    description: "Maternal Weight - Maternal Effect",
  },
  {
    name: "bw_de",
    label: "BW - DE",
    description: "Birth Weight - Direct Effect",
  },
  {
    name: "ww_de",
    label: "WW - DE",
    description: "Weaning Weight - Direct Effect",
  },
  {
    name: "aw_de",
    label: "AW - DE",
    description: "Annual Weight - Direct Effect",
  },
  {
    name: "yw_de",
    label: "YW - DE",
    description: "Yearling Weight - Direct Effect",
  },
  {
    name: "sc365",
    label: "SC365",
    description: "Scrotal Perimeter at 365 days",
  },
  {
    name: "sc450",
    label: "SC450",
    description: "Scrotal Perimeter at 450 days",
  },
  {
    name: "bs",
    label: "B.S",
    description: "Body Structure",
  },
  {
    name: "prec",
    label: "PREc",
    description: "Precocity",
  },
  {
    name: "musc",
    label: "MUSC",
    description: "Muscularity",
  },
  {
    name: "rea",
    label: "REA",
    description: "Rib Eye Area",
  },
  {
    name: "finishing",
    label: "Finishing",
    description: "Finishing",
  },
  {
    name: "mar",
    label: "MAR",
    description: "Marbling",
  },
  {
    name: "iabcz",
    label: "iABCZ",
    description: "iABCZ Index",
  },
];

const measurements: MeasurementData[] = [
  {
    name: "length_of_rump",
    label: "Length of Rump",
    acronym: "LP",
    measures: "Length from hip to pin bones (CG)",
  },
  {
    name: "height_of_rump",
    label: "Height of Rump",
    acronym: "AG",
    measures: "Vertical distance from the ground to the top of the rump",
  },
  {
    name: "lower_height",
    label: "Lower Height",
    acronym: "LH",
    measures: "ALTURA INFERIOR - Distance from ground to bottom of chest",
  },
  {
    name: "back_height",
    label: "Back Height",
    acronym: "BH",
    measures: "Height at withers (AG)",
  },
  {
    name: "body_length",
    label: "Body Length",
    acronym: "BL",
    measures: "Length from point of shoulder to pin bone (CC)",
  },
  {
    name: "rib_depth",
    label: "Rib Depth",
    acronym: "RD",
    measures: "Depth of rib cage (PC)",
  },
  {
    name: "chest_perimeter",
    label: "Chest Perimeter",
    acronym: "CP",
    measures: "Circumference of chest behind shoulders (PT)",
  },
  {
    name: "body_width",
    label: "Body Width",
    acronym: "BW",
    measures: "Width between hip bones (LG)",
  },
  {
    name: "scrotal_circumference",
    label: "Scrotal Circumference",
    acronym: "SC",
    measures: "Circumference of scrotum (CE)",
  },
];

type MeasurementData = {
  name: string;
  label: string;
  acronym: string;
  measures: string;
};

export function FormSummary({ img }: Props) {
  const { getValues, control } = useFormContext();
  const formData = getValues();
  const bullType = getValues("bullType");
  const [selectedImage, setSelectedImage] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  // let previewURL
  // if ( img ) {
  // 	previewURL = URL.createObjectURL ( img.map ( image ) )
  // }

  console.log(getValues());

  useEffect(() => {
    console.log(img);

    const newUrls = img ? img.map((file) => URL.createObjectURL(file)) : [];
    setPreviewUrls(newUrls);
    // Cleanup function
    return () => {
      newUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [img]);
  const formatDate = (dateString: string) => {
    if (!dateString) return "Not provided";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const formatAccuracy = (value: any) => {
    if (value === undefined || value === null || value === "") {
      return <span className="text-muted-foreground italic">Not provided</span>;
    }
    return `${(Number(value) * 100).toFixed(1)}%`;
  };

  const renderValue = (value: any, maxLength = 100) => {
    if (value === undefined || value === null || value === "") {
      return <span className="text-muted-foreground italic">Not provided</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-muted-foreground italic">None</span>;
      }
      return value.join(", ");
    }

    // For string values, truncate if too long
    if (typeof value === "string" && value.length > maxLength) {
      return (
        <span title={value} className="cursor-help">
          {value.substring(0, maxLength)}...
        </span>
      );
    }

    return value;
  };

  return (
    <div className="w-full space-y-6 w-[800px]">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Registration Summary</h2>
        <p className="text-muted-foreground">
          Please review the information before submitting
        </p>
      </div>

      <div className="grid gap-4">
        <Accordion
          type="single"
          collapsible
          defaultValue="basic"
          className="w-full"
        >
          <AccordionItem value="basic">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Basic Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm overflow-hidden">
                  <div className="font-medium">DGR:</div>
                  <div className="truncate">{renderValue(formData.dgr)}</div>

                  <div className="font-medium">Birth Date:</div>
                  <div>
                    {renderValue(
                      formData.birthDate
                        ? formatDate(formData.birthDate)
                        : null,
                    )}
                  </div>

                  <div className="font-medium">Sex:</div>
                  <div>{renderValue(formData.sex)}</div>

                  <div className="font-medium">Name:</div>
                  <div className="truncate">{renderValue(formData.name)}</div>

                  <div className="font-medium">Age (months):</div>
                  <div>{renderValue(formData.age)}</div>

                  <div className="font-medium">Average Daily Gain:</div>
                  <div>{renderValue(formData.adg)}</div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="physical">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Physical Attributes</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm overflow-hidden">
                  <div className="font-medium">Weight at 120 days:</div>
                  <div>
                    {renderValue(formData.w120)} {formData.w120 && "kg"}
                  </div>

                  <div className="font-medium">Weaning Weight:</div>
                  <div>
                    {renderValue(formData.ww)} {formData.ww && "kg"}
                  </div>

                  <div className="font-medium">Pre-weaning Weight:</div>
                  <div>
                    {renderValue(formData.pw)} {formData.pw && "kg"}
                  </div>

                  <div className="font-medium">Yearling Weight:</div>
                  <div>
                    {renderValue(formData.yw)} {formData.yw && "kg"}
                  </div>

                  <div className="font-medium">Post-Milk Weight Gain:</div>
                  <div>
                    {renderValue(formData.pmwg)} {formData.pmwg && "kg"}
                  </div>

                  <div className="font-medium">Coat Color:</div>
                  <div>{renderValue(formData.coatColor)}</div>

                  <div className="font-medium">Frame Size:</div>
                  <div>{renderValue(formData.size)}</div>

                  <div className="font-medium">Current Weight:</div>
                  <div>
                    {renderValue(formData.weight)} {formData.weight && "kg"}
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="genetic">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Genetic Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm overflow-hidden">
                  <div className="font-medium">Paternal Nucleus:</div>
                  <div className="truncate">
                    {renderValue(formData.paternalNucleus)}
                  </div>

                  <div className="font-medium">Paternal Type:</div>
                  <div>{renderValue(formData.paternalType)}</div>

                  <div className="font-medium">Breeding Type:</div>
                  <div>{renderValue(formData.breedingType)}</div>

                  <div className="font-medium">Vaccination:</div>
                  <div className="break-words">
                    {renderValue(formData.vaccination)}
                  </div>

                  <div className="font-medium">Racial Composition:</div>
                  <div>{renderValue(formData.racialComposition)}</div>

                  <div className="font-medium">Birth Condition:</div>
                  <div>{renderValue(formData.birthCondition)}</div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="parental">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Parental Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm overflow-hidden">
                  <div className="font-medium">Dam Name:</div>
                  <div className="truncate">
                    {renderValue(formData.damName)}
                  </div>

                  <div className="font-medium">Dam DGR:</div>
                  <div className="truncate">{renderValue(formData.damDGR)}</div>

                  <div className="font-medium">Dam Birth Date:</div>
                  <div>
                    {renderValue(
                      formData.damBirthDate
                        ? formatDate(formData.damBirthDate)
                        : null,
                    )}
                  </div>

                  <div className="font-medium">Dam Breed:</div>
                  <div>{renderValue(formData.damBreed)}</div>

                  <div className="font-medium">Sire Name:</div>
                  <div className="truncate">
                    {renderValue(formData.sireName)}
                  </div>

                  <div className="font-medium">Sire DGR:</div>
                  <div className="truncate">
                    {renderValue(formData.sireDGR)}
                  </div>

                  <div className="font-medium">Sire Birth Date:</div>
                  <div>
                    {renderValue(
                      formData.sireBirthDate
                        ? formatDate(formData.sireBirthDate)
                        : null,
                    )}
                  </div>

                  <div className="font-medium">Sire Breed:</div>
                  <div>{renderValue(formData.sireBreed)}</div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="grandparental">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Grandparental Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 space-y-4 text-sm overflow-hidden">
                  <div>
                    <h4 className="font-semibold mb-2">
                      Paternal Grandparents
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 pl-0 sm:pl-4">
                      <div className="font-medium">Grandam Name:</div>
                      <div className="truncate">
                        {renderValue(formData.paternalGrandDamName)}
                      </div>

                      <div className="font-medium">Grandam DGR:</div>
                      <div className="truncate">
                        {renderValue(formData.paternalGrandDamDGR)}
                      </div>

                      <div className="font-medium">Grandam Breed:</div>
                      <div>{renderValue(formData.paternalGrandDamBreed)}</div>

                      <div className="font-medium">Grandsire Name:</div>
                      <div className="truncate">
                        {renderValue(formData.paternalGrandSireName)}
                      </div>

                      <div className="font-medium">Grandsire DGR:</div>
                      <div className="truncate">
                        {renderValue(formData.paternalGrandSireDGR)}
                      </div>

                      <div className="font-medium">Grandsire Breed:</div>
                      <div>{renderValue(formData.paternalGrandSireBreed)}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">
                      Maternal Grandparents
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 pl-0 sm:pl-4">
                      <div className="font-medium">Grandam Name:</div>
                      <div className="truncate">
                        {renderValue(formData.maternalGrandDamName)}
                      </div>

                      <div className="font-medium">Grandam DGR:</div>
                      <div className="truncate">
                        {renderValue(formData.maternalGrandDamDGR)}
                      </div>

                      <div className="font-medium">Grandam Breed:</div>
                      <div>{renderValue(formData.maternalGrandDamBreed)}</div>

                      <div className="font-medium">Grandsire Name:</div>
                      <div className="truncate">
                        {renderValue(formData.maternalGrandSireName)}
                      </div>

                      <div className="font-medium">Grandsire DGR:</div>
                      <div className="truncate">
                        {renderValue(formData.maternalGrandSireDGR)}
                      </div>

                      <div className="font-medium">Grandsire Breed:</div>
                      <div>{renderValue(formData.maternalGrandSireBreed)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>

          {bullType === "Beef Bull" ? (
            <>
              <AccordionItem value="genomicdata">
                <AccordionTrigger className="text-lg font-medium">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="truncate">Genomic Data</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <h2 className="text-2xl font-bold text-center mb-6">
                    Genetic Breeding Values
                  </h2>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[180px]">Trait</TableHead>
                        <TableHead>EPD</TableHead>
                        <TableHead>ACC.</TableHead>
                        <TableHead>DECA</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {traits.map((trait) => (
                        <TableRow key={trait.name}>
                          <TableCell className="font-medium">
                            <div>
                              <div>{trait.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {trait.description}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={control}
                              name={`${trait.name}_epd`}
                              render={({ field }) => (
                                <FormItem className="space-y-0">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="EPD"
                                      {...field}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      className="w-full"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={control}
                              name={`${trait.name}_acc`}
                              render={({ field }) => (
                                <FormItem className="space-y-0">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="ACC"
                                      {...field}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      className="w-full"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <FormField
                              control={control}
                              name={`${trait.name}_deca`}
                              render={({ field }) => (
                                <FormItem className="space-y-0">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="DECA"
                                      {...field}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      className="w-full"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="bodymeasurements">
                <AccordionTrigger className="text-lg font-medium">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="truncate">Body Measurement</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <h2 className="text-2xl font-bold text-center mb-6">
                    Genetic Breeding Values
                  </h2>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Measurement</TableHead>
                        <TableHead className="w-[100px]">Acronym</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Measures
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {measurements.map((measurement) => (
                        <TableRow key={measurement.name}>
                          <TableCell className="font-medium">
                            {measurement.label}
                          </TableCell>
                          <TableCell>{measurement.acronym}</TableCell>
                          <TableCell>
                            <FormField
                              control={control}
                              name={measurement.name}
                              render={({ field }) => (
                                <FormItem className="space-y-0">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder="Enter value"
                                      {...field}
                                      onChange={(e) =>
                                        field.onChange(
                                          e.target.value
                                            ? Number(e.target.value)
                                            : undefined,
                                        )
                                      }
                                      className="w-full"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                            {measurement.measures}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionContent>
              </AccordionItem>
            </>
          ) : (
            <AccordionItem value="milk">
              <AccordionTrigger className="text-lg font-medium">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="truncate">Milk Composition</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Card>
                  <CardContent className="p-4 space-y-4 text-sm overflow-hidden">
                    <div>
                      <h4 className="font-semibold mb-2">Production Metrics</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 pl-0 sm:pl-4">
                        <div className="font-medium">Milk Production:</div>

                        <div className="font-medium">Milk Accuracy:</div>
                        <div>{formatAccuracy(formData.milkAcc)}</div>
                        <div className="font-medium">Age 1 P Accuracy:</div>
                        <div>{formatAccuracy(formData.age1pAcc)}</div>

                        <div className="font-medium">EBI Accuracy:</div>
                        <div>{formatAccuracy(formData.ebiAcc)}</div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">
                        Protein Composition
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 pl-0 sm:pl-4">
                        <div className="font-medium">Beta-Lactoglobulin:</div>
                        <div>{renderValue(formData.betaLac)}</div>

                        <div className="font-medium">Kappa-Casein:</div>
                        <div>{renderValue(formData.kappaC)}</div>

                        <div className="font-medium">Beta-Casein:</div>
                        <div>{renderValue(formData.betaC)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="status">
            <AccordionTrigger className="text-lg font-medium">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="truncate">Status Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <Card>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm overflow-hidden">
                  <div className="font-medium">Animal Status:</div>
                  <div>{renderValue(formData.animalStatus)}</div>

                  <div className="font-medium">Birth Weight:</div>
                  <div>
                    {renderValue(formData.birthWeight)}{" "}
                    {formData.birthWeight && "kg"}
                  </div>

                  <div className="font-medium">Farm Location:</div>
                  <div>{renderValue(formData.farm)}</div>

                  <div className="font-medium">Assigned Supervisor:</div>
                  <div>{renderValue(formData.assignedSupervisor)}</div>

                  <div className="font-medium">Cattle Class:</div>
                  <div>{renderValue(formData.cattleClass)}</div>

                  <div className="font-medium">Production Phase:</div>
                  <div>{renderValue(formData.phases)}</div>

                  <div className="font-medium">Health Diagnosis:</div>
                  <div>{renderValue(formData.diagnosis)}</div>

                  <div className="font-medium">Insemination Program:</div>
                  <div>{renderValue(formData.inseminationProgram)}</div>

                  <div className="font-medium">Number of Offspring:</div>
                  <div>{renderValue(formData.numberOffspring)}</div>

                  <div className="font-medium">Efficiency Index:</div>
                  <div>{renderValue(formData.indexEfficiency)}</div>

                  <div className="font-medium sm:col-span-2">
                    Slaughter Information:
                  </div>
                  <div className="sm:col-span-2 break-words">
                    {renderValue(formData.slaughterInfo)}
                  </div>

                  <div className="font-medium">Farmer ID:</div>
                  <div className="truncate">
                    {renderValue(formData.farmerID)}
                  </div>

                  <div className="font-medium">Farm ID:</div>
                  <div className="truncate">{renderValue(formData.farmID)}</div>

                  <div className="font-medium sm:col-span-2">Image URL:</div>
                  <div className="sm:col-span-2 break-all text-xs">
                    {renderValue(formData.url)}
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* {formData.url && ( */}
      {/*   <div className="mt-4"> */}
      {/*     <h3 className="text-lg font-medium mb-2">Animal Image</h3> */}
      {/*     {img?.map((image) => ( */}
      {/*       <div className="border rounded-lg overflow-hidden w-full max-w-md mx-auto aspect-video relative"> */}
      {/*         <Image */}
      {/*           src={ */}
      {/*             URL.createObjectURL(image) */}
      {/*               ? URL.createObjectURL(image) */}
      {/*               : "https://ui.shadcn.com/placeholder.svg" */}
      {/*           } */}
      {/*           alt={formData.name || "Animal"} */}
      {/*           className="w-full shadow-2xl shadow-orange-700 h-full object-cover */}
      {/* 							absolute inset-0" */}
      {/*           // 		onError={ ( e ) => { */}
      {/*           // 	;( */}
      {/*           // 			e.target as HTMLImageElement */}
      {/*           // ).src */}
      {/*           // = */}
      {/*           // 			"/placeholder.svg?height=300&width=400" */}
      {/*           // } } isBlurred */}
      {/*           isZoomed */}
      {/*           // className={ "shadow-2xl */}
      {/*           // shadow-orange-700" } */}
      {/*           height={350} */}
      {/*           loading="lazy" */}
      {/*           shadow="sm" */}
      {/*           sizes="lg" */}
      {/*           width={350} */}
      {/*         /> */}
      {/*       </div> */}
      {/*     ))} */}
      {/*   </div> */}
      {/* )} */}
      {previewUrls && previewUrls.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-center mb-4">
            Animal Image Preview
          </h3>

          {/* Main Preview Image */}
          <div className="border rounded-lg overflow-hidden w-full max-w-md mx-auto aspect-video relative mb-4">
            <Image
              src={previewUrls[selectedImage]}
              alt={formData.name || `Preview of ${previewUrls[selectedImage]}`}
              className="w-full h-full object-cover absolute inset-0"
              isZoomed
              loading="lazy"
              shadow="sm"
            />
          </div>

          {/* Thumbnail Gallery */}
          {/* Show thumbnails only if there is more than one image */}
          {previewUrls.length > 1 && (
            <div className="flex justify-center items-center gap-3 flex-wrap">
              {previewUrls.map((url, index) => (
                <Button
                  key={url} // Using the unique URL as a key is more
                  // stable
                  type="button"
                  onClick={() => setSelectedImage(index)}
                  className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                    selectedImage === index
                      ? "border-orange-500 ring-2 ring-orange-200"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {/* Use the stable URL from our state here as well */}
                  <img
                    src={url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
